// One trip's worth of Captain's Log entries: every entry sharing a Trip Name,
// on one map, with what the trip added up to.
//
// A "trip" here is just the shared value of the type's own trip field -- there
// is no TaskPogoTrip row behind it and no foreign key (see LogbookTrips.mjs).
// The name comes off the URL, so a deep link works and this page never depends
// on the list page having loaded first.
//
// Read-only, deliberately. This is the group map's contract: a picture of the
// whole trip plus a navigator, never an editor. Every edit happens on an
// entry's own page, which owns the write-back of a gazetteer resolve -- so the
// resolve here must never be persisted from this page.
//
// The load and the gazetteer/map-leg chain are shared with the per-year page
// (LogbookYear.jsx) as useLogbookPageEntries / useEntryMapLegs -- they encode
// rules, not boilerplate, and a second copy would miss the next fix to either.

import { useCallback, useMemo, useState, Suspense } from 'react';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { useParams, useLocation } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import * as Database from '../../utilities/Database.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { entryLane } from '../../utilities/saveLanes';
import CombineTripsModal from '../trip/CombineTripsModal';
import RenameTripModal from '../trip/RenameTripModal';
import ShareLogbookTripModal from './ShareLogbookTripModal';
import {
  shareLogbookTripAvailable,
  logbookTripSharesLostByMerge,
  repointLogbookTripShares,
} from '../../utilities/shareLogbookTrip.mjs';
import { displayDate } from '../../fleet-shared/Common.mjs';
import {
  urlForLogbookEntry,
  urlForLogbookForId,
  urlForLogbookTrip,
} from '../../fleet-shared/Urls.mjs';
import {
  flattenEntry,
  listColumnsForType,
  parseEntryFields,
  splitEntryFields,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import {
  entriesForTripKey,
  entryTripKey,
  summarizeTripEntries,
  tripFieldForType,
  tripNameOf,
  tripSummaryParts,
  tripsInLogbook,
} from '../../fleet-shared/LogbookTrips.mjs';
import {
  groupNameFromKey,
  isNamedGroupKey,
  isTripGroupIdKey,
  tripGroupIdFromKey,
} from '../../fleet-shared/TripIdentity.mjs';
import {
  adjacentTrips,
  mergedTripIdentity,
  recordsToRefile,
} from '../../fleet-shared/TripMerge.mjs';
import { tripGroupDateRange } from '../../fleet-shared/TripGroups.mjs';
import { ENTRY_ROUTE_KEY } from '../../fleet-shared/TripRoute.mjs';
import { sortTripsAscending } from '../../fleet-shared/TripFields.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { useLogbookPageEntries } from './useLogbookPageEntries';
import { useEntryMapLegs } from './useEntryMapLegs';
import LogbookEntriesTable from './LogbookEntriesTable';
import LogbookViewControls from './LogbookViewControls';
import PhotoErrorAlert from './PhotoErrorAlert';
import { useLogbookViewPrefs } from './useLogbookViewPrefs';
import { useEntryPhotos } from './useEntryPhotos';

// Lazy: the only import path that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

// What a Combine button calls the trip on the other side of it.
//
// An unfiled entry's row has no name at all -- tripsInLogbook leaves it '',
// because the "(no trip name)" wording belongs to a page -- and a button that
// read Combine with "" would say nothing about what it was about to absorb. So
// the row is DATED instead, which is the only thing that distinguishes one
// unfiled entry from another.
function combineLabel(row) {
  if (row?.name) return row.name;
  const when = displayDate(row?.startDate);
  return when ? `the entry on ${when}` : 'the entry with no trip name';
}

// "3 entries" / "1 entry", for the Combine dialog's description of a trip.
function entryCountLabel(count) {
  if (!count) return '';
  return `${count} ${count === 1 ? 'entry' : 'entries'}`;
}

const LogbookTrip = () => {
  const { itemId, logbookId, tripKey } = useParams();
  const location = useLocation();
  const navigate = useRootCrumbNavigate();

  const decodedKey = decodeURIComponent(tripKey ?? '');

  const select = useCallback(
    (allEntries, loadedLogbook) =>
      entriesForTripKey(
        allEntries,
        decodedKey,
        loadedLogbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE
      ),
    [decodedKey]
  );

  const {
    item, logbook, entries, setEntries, allEntries, setAllEntries, StatusMessage,
  } = useLogbookPageEntries({
    itemId,
    logbookId,
    selectKey: decodedKey,
    select,
  });

  // Two readers now: the map's route overlay, and the entries table's From/To
  // columns (see cellValue).
  const placeIndex = usePlaceIndex();

  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;

  // The type's trip field: the column to drop from the table (Trip Name is
  // redundant inside one trip -- it's the page heading), the key Rename writes
  // to, and the single expression deciding whether this type has trips at all.
  // Declared up here because the trip rows below are gated on it.
  const tripField = useMemo(() => tripFieldForType(typeId), [typeId]);

  const [showRename, setShowRename] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // The persisted Show notes switch. No Entries/Trips toggle here -- the page
  // IS one trip.
  const [viewPrefs, setViewPref] = useLogbookViewPrefs();

  // The photos attached to this logbook's entries. `itemId` is off useParams,
  // so the S3 listing overlaps the GraphQL round trip rather than waiting on
  // it. The switch is the hook's `enabled` AND what the table is given below,
  // so "we fetched" and "we render" cannot disagree -- and turning it off is a
  // mount gate on the read, not just a display filter.
  const { photosByEntry, photosError, openPhoto } = useEntryPhotos(itemId, {
    enabled: viewPrefs.showPhotos,
  });
  // Which neighbour a Combine was started against: { direction, row }.
  const [combining, setCombining] = useState(null);
  // The share links that combine would REVOKE, asked before the merge -- "will
  // this revoke a link?" is only useful while it can still be answered no, and
  // a revoked link cannot be brought back.
  const [combineLosses, setCombineLosses] = useState([]);

  // Open the Combine dialog against a neighbour, having first found out what it
  // would cost. TripGroup.openCombine's shape: the read is best-effort and
  // never blocks the dialog, because the worst case is a warning the user did
  // not get and they can still see the link's state afterwards.
  const openCombine = useCallback(
    (direction, row) => {
      setCombineLosses([]);
      setCombining({ direction, row });
      if (!shareLogbookTripAvailable()) return;
      logbookTripSharesLostByMerge(logbookId, [decodedKey, row.key], decodedKey)
        .then((lost) => {
          setCombineLosses(
            lost.length > 0
              ? ["Both trips have a share link. The other trip's link will stop working."]
              : []
          );
        })
        .catch(() => setCombineLosses([]));
    },
    [logbookId, decodedKey]
  );

  // Every trip in this logbook, as rows the merge rules understand. The whole
  // logbook is already in hand -- useLogbookPageEntries reads it and narrows it
  // to this trip -- so this used to be a SECOND read of the same table, fired
  // when the rename dialog opened. Null until that fetch lands, so the buttons
  // and the collision warning both simply appear when it does.
  const tripRows = useMemo(() => {
    if (!allEntries || !tripField) return null;
    return tripsInLogbook(allEntries, typeId).map((row) => ({
      key: row.key,
      name: row.name,
      named: isNamedGroupKey(row.key),
      startDate: row.summary.startDate,
      endDate: row.summary.endDate,
      count: row.summary.count,
    }));
    // tripField is read only as a gate; typeId is what actually keys the rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEntries, typeId, tripField]);

  // The trips immediately before and after this one. Unfiled entries ('e:'
  // rows) count: pulling a stray day into the trip around it is the commonest
  // reason to combine anything.
  const neighbours = useMemo(
    () => (tripRows ? adjacentTrips(tripRows, decodedKey) : { previous: null, next: null }),
    [tripRows, decodedKey]
  );

  // The logbook's OTHER trip names, for the rename dialog's collision warning.
  // Scoped to the logbook rather than the item for the reason the buckets are:
  // two Captain's Logs on one boat are separate universes.
  const otherTripNames = useMemo(() => {
    if (!tripRows) return null;
    return tripRows
      .filter((row) => row.named && row.key !== decodedKey)
      .map((row) => row.name)
      .filter(Boolean);
  }, [tripRows, decodedKey]);

  // Oldest-first, for the MAP and the heading -- not the table. TripGroupMap
  // hangs the trip's End pill on the LAST leg and cycles leg colours by index,
  // and the heading is the trip's first-seen spelling, so both need the legs in
  // the order they were sailed. Both sources here (the handover and the fetch)
  // come back newest-first -- listCompletedTasksForLogbook queries DESC for the
  // logbook LIST's sake -- so reversing mirrors them exactly, and that mirror
  // is the only thing that orders legs sharing one date (a day with three hops
  // is ordinary, and `date` alone can't separate them). The sort then pins the
  // date order regardless of what sortDirection the query uses tomorrow; it's
  // stable, so it leaves the reversed ties alone.
  //
  // The table reads NEWEST-first (`newestFirst` below), like the logbook list
  // this page is reached from and the Trips page's card.
  const orderedEntries = useMemo(
    () => (entries ? sortTripsAscending([...entries].reverse()) : null),
    [entries]
  );

  const { newestFirst, mapLegs, anyOnMap } = useEntryMapLegs(orderedEntries, placeIndex);

  const summary = useMemo(
    () => (entries ? summarizeTripEntries(entries, typeId) : null),
    [entries, typeId]
  );

  const columns = useMemo(
    () => listColumnsForType(typeId).filter((field) => field.key !== tripField?.key),
    [typeId, tripField]
  );

  // Open an entry, handing over only item + logbook.
  //
  // Deliberately NOT the entry itself: useSharedItemPageState would consume it
  // and skip the fetch, and this page's copy predates any edit made since. And
  // deliberately re-looked-up from `entries` rather than taken off the display
  // copies (the table rows are `newestFirst` elements now, as well as the
  // map's legs) -- see useEntryMapLegs' header for why that matters.
  const onSelectEntry = useCallback(
    (leg) => {
      const stored = (entries || []).find((entry) => entry.id === leg.id);
      if (!stored) return;
      navigate(urlForLogbookEntry(itemId, logbookId, stored.id), {
        state: { item, logbook, fromLogbookList: true },
      });
    },
    [entries, itemId, logbookId, item, logbook, navigate]
  );

  // The trip's name. A 'g:' key carries none -- that is the point of it -- so
  // this comes from the entries: the first-seen spelling, the same label
  // tripsInYear puts on its rows, so a row and the page it opens agree.
  //
  // Router state seeds it because the fetch is a round trip and a heading that
  // arrives second reads as a page that loaded wrong. A legacy 'n:' key still
  // has the name in it as a last resort, lowercased -- worth showing for the
  // moment before the entries land, and replaced by the real spelling the
  // instant they do.
  const heading = useMemo(() => {
    const spelled = orderedEntries?.length ? tripNameOf(orderedEntries[0], typeId).trim() : '';
    return spelled || location.state?.tripName || groupNameFromKey(decodedKey) || 'Trip';
  }, [orderedEntries, typeId, location.state, decodedKey]);

  // Rename the trip: write the new Trip Name to every entry.
  //
  // TripGroup.handleRename's counterpart, and the same shape for the same
  // reason -- a trip is not a row, the name lives on each record, so renaming
  // one touches all of them. Throws on failure; the dialog reports it inline.
  //
  // What makes this a RENAME rather than a re-filing is that each entry keeps
  // its `tripGroupId`: flattenEntry hoists it, splitEntryFields emits it again
  // (the field's `idKey`), so the binding rides through untouched. Doing the
  // same thing by hand in the Trip Name box would do the opposite -- typing a
  // diverging name unbinds, which is how you MOVE an entry to another trip.
  // The two gestures are deliberately distinct and this is the one that keeps
  // ten entries together.
  async function handleRename(name) {
    // Wait out any entry whose own edit is still in flight, then read fresh:
    // a queued name from that entry's page landing after this would drop it
    // back into the old trip. Same rule loadGroupLegs applies on the leg side.
    const lanes = (entries ?? []).map((entry) => entryLane(entry.id));
    if (lanes.some((lane) => saveQueue.isPending(lane))) {
      await saveQueue.settledAll(lanes);
    }
    const fresh = entriesForTripKey(
      await Database.listCompletedTasksForLogbook(logbookId),
      decodedKey,
      typeId
    );

    // Entries already spelling it exactly are left alone -- a rename that only
    // changes capitalization still has to write the rest.
    const targets = fresh.filter((entry) => tripNameOf(entry, typeId) !== name);

    // allSettled: these are independent writes, and reporting the first
    // rejection while the others are still in flight would leave the caller
    // repairing a moving database.
    const results = await Promise.allSettled(targets.map((entry) => {
      const flat = flattenEntry(entry, typeId);
      flat[tripField.key] = name;
      // `preserve` is not optional: an AWSJSON write replaces the whole blob,
      // so without the entry's current one this would delete its geo route.
      const payload = splitEntryFields(flat, typeId, { preserve: entry.fields });
      payload.id = entry.id;
      payload.itemId = entry.itemId;
      if (entry.logbookId) payload.logbookId = entry.logbookId;
      return Database.updateCompletedTask(payload);
    }));

    const failed = results.find((r) => r.status === 'rejected');
    if (failed) {
      // A partial failure SPLITS the trip: some entries carry the new name and
      // some the old. For a bound trip that is cosmetic -- the id still holds
      // them together and the heading just shows the first-seen spelling -- but
      // for a legacy name-keyed one it is two trips, so re-read either way and
      // let the dialog say what went wrong.
      //
      // Narrowed through the same rule `select` uses, because `entries` IS the
      // trip's slice everywhere downstream -- the table, the map legs, the
      // totals and the heading all read it as "just this trip". Handing it the
      // whole logbook would leave a corrupted page behind the dialog. For the
      // legacy split it is also the more correct answer: the entries that DID
      // rename now key under the new name, so narrowing by the key this page is
      // still addressed by leaves exactly the ones that still belong to it.
      const all = await Database.listCompletedTasksForLogbook(logbookId);
      setAllEntries(all);
      setEntries(entriesForTripKey(all, decodedKey, typeId));
      throw failed.reason;
    }

    const saved = results.map((r) => r.value);
    const byId = new Map(saved.filter((row) => row?.id).map((row) => [row.id, row]));
    const renamed = fresh.map((entry) => byId.get(entry.id) ?? entry);

    setShowRename(false);
    setEntries(renamed);
    // The logbook's copy carries these entries too, and the Combine buttons and
    // the collision warning are both derived from it -- so it has to take the
    // new name here or it would still describe this trip as it was.
    setAllEntries((current) =>
      (current === null ? current : current.map((entry) => byId.get(entry.id) ?? entry))
    );

    // A trip with an id does not move when it is renamed -- that is the point
    // of the id, and this page's URL still addresses it. Only a legacy
    // name-keyed trip re-addresses, and the key comes off a WRITTEN entry
    // rather than being rebuilt from the name, so it can't disagree with what
    // was stored.
    const nextKey = renamed.length > 0 ? entryTripKey(renamed[0], typeId) : decodedKey;
    if (nextKey !== decodedKey) {
      // The key moved, so a share row pointing at the old one is stranded --
      // and a stranded 'n:' key is worse than untidy, because it still resolves
      // BY NAME onto whatever trip is spelled that way next. Only a legacy
      // name-keyed trip gets here: a bound trip does not re-address when it is
      // renamed, which is the whole point of the id.
      //
      // Best-effort: the entry writes have already landed, so a failure here
      // must not turn a completed rename into an error the user is invited to
      // retry. repointLogbookTripShares swallows and logs its own.
      await repointLogbookTripShares(logbookId, [decodedKey], nextKey);
      navigate(urlForLogbookTrip(itemId, logbookId, nextKey), {
        replace: true,
        state: { item, logbook, tripName: name, entries: renamed },
      });
    }
  }

  // Combine this trip with the one before or after it: write one trip name AND
  // one tripGroupId to every entry of both.
  //
  // handleRename's shape with one line added, and that line is the whole
  // difference between the two gestures. Rename leaves `tripGroupId` alone, so
  // ten entries stay the trip they were; this writes it, which is what MOVES
  // them. Doing the same thing by hand in one entry's Trip Name box moves that
  // one entry -- typing a diverging name unbinds it -- and this is the version
  // that takes the whole trip with it.
  //
  // Throws on failure; the dialog reports it inline.
  async function handleCombine(targetKey, name) {
    // Wait out BOTH trips' in-flight edits, not just this page's: a queued name
    // from an entry on the other trip landing after this would drop it straight
    // back out of the combined trip.
    const known = [...(entries ?? []), ...(allEntries ?? [])];
    const lanes = [...new Set(known.map((entry) => entryLane(entry.id)))];
    if (lanes.some((lane) => saveQueue.isPending(lane))) {
      await saveQueue.settledAll(lanes);
    }

    const all = await Database.listCompletedTasksForLogbook(logbookId);
    // Rows, not entriesForTripKey: that refuses any key other entries could not
    // join, so it answers [] for the unfiled 'e:' entry which is the commonest
    // thing to fold into a trip.
    const rows = tripsInLogbook(all, typeId);
    const source = rows.find((row) => row.key === decodedKey)
      ?? rows.find((row) => row.key === entryTripKey(entries?.[0] ?? {}, typeId));
    const target = rows.find((row) => row.key === targetKey);
    if (!source || !target || source.key === target.key) {
      throw new Error('These trips have changed since the page loaded. Reload and try again.');
    }

    // The SOURCE's id wins -- see mergedTripIdentity. Read off the KEY rather
    // than off an entry, so it cannot disagree with the key the page is
    // addressed by.
    const identity = mergedTripIdentity({
      sourceGroupId: tripGroupIdFromKey(source.key),
      targetGroupId: tripGroupIdFromKey(target.key),
      name,
    });

    const merged = [...source.entries, ...target.entries];
    const targets = recordsToRefile(merged, identity, (entry) => ({
      name: tripNameOf(entry, typeId),
      tripGroupId: parseEntryFields(entry)[tripField.idKey],
    }));

    const results = await Promise.allSettled(targets.map((entry) => {
      const flat = flattenEntry(entry, typeId);
      flat[tripField.key] = identity.name;
      flat[tripField.idKey] = identity.tripGroupId ?? null;
      // `preserve` is not optional here either: an AWSJSON write replaces the
      // whole blob, so without the entry's current one this would delete its
      // geo route.
      const payload = splitEntryFields(flat, typeId, { preserve: entry.fields });
      payload.id = entry.id;
      payload.itemId = entry.itemId;
      if (entry.logbookId) payload.logbookId = entry.logbookId;
      return Database.updateCompletedTask(payload);
    }));

    const failed = results.find((r) => r.status === 'rejected');
    if (failed) {
      // A partial failure leaves some entries refiled and some not, which is
      // still two trips. Re-read both copies and narrow through the same rule
      // `select` uses, so what is behind the dialog is what is stored.
      const reloaded = await Database.listCompletedTasksForLogbook(logbookId);
      setAllEntries(reloaded);
      setEntries(entriesForTripKey(reloaded, decodedKey, typeId));
      throw failed.reason;
    }

    const saved = results.map((r) => r.value);
    const byId = new Map(saved.filter((row) => row?.id).map((row) => [row.id, row]));
    const written = merged.map((entry) => byId.get(entry.id) ?? entry);

    setCombining(null);

    // Off a WRITTEN entry, never rebuilt from the name. For a bound source this
    // is the key the page is already on, which is the whole reason the source's
    // id wins: a bound trip does not re-address when it is edited.
    const nextKey = entryTripKey(written[0], typeId);

    // Two trips' share rows have just landed on one key, and AT MOST ONE may
    // survive there -- two rows on a key is a live public URL its owner can no
    // longer see or revoke (getLogbookTripShareForKey answers with items[0]).
    // Run on BOTH paths below, not just the re-addressing one: the keys collide
    // either way. `decodedKey` first, so the link published for the trip the
    // user is standing on is the one that keeps working.
    await repointLogbookTripShares(logbookId, [decodedKey, targetKey], nextKey);

    if (nextKey === decodedKey) {
      // Nothing reloads on this path, so refresh both copies or the Combine
      // buttons would still offer the trip that was just absorbed.
      setEntries(written);
      setAllEntries((current) =>
        (current === null ? current : current.map((entry) => byId.get(entry.id) ?? entry))
      );
      return;
    }
    navigate(urlForLogbookTrip(itemId, logbookId, nextKey), {
      replace: true,
      state: { item, logbook, tripName: identity.name, entries: written },
    });
  }

  return (
    <div className="pb-2">
      <ItemBreadcrumbs
        item={item}
        extras={[
          ...(logbook ? [{ name: logbook.name, url: urlForLogbookForId(itemId, logbookId) }] : []),
          { name: heading, url: urlForLogbookTrip(itemId, logbookId, decodedKey) },
        ]}
      />

      <StatusMessage />

      {anyOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap
            legs={mapLegs}
            onSelectLeg={onSelectEntry}
            routeKey={ENTRY_ROUTE_KEY}
            title="Route"
            showWeather={false}
          />
        </Suspense>
      )}

      <DataCard
        header={
          <CardHeader
            text={heading}
            // The two actions on this page that are about the TRIP rather than
            // about one entry -- everything else here opens an entry. Hidden
            // until there is something to act on: the name is written to each
            // entry, and a type with no trip field has no trip at all.
            //
            // Share is additionally gated on the backend being deployed, the
            // weatherAvailable() pattern: the model, the Lambda and the Function
            // URL land in one deploy, so the URL's presence in
            // amplify_outputs.json is what says the schema is live.
            extraActions={
              tripField && entries && entries.length > 0 ? (
                <>
                  {shareLogbookTripAvailable() && (
                    <Button variant="outline-secondary" onClick={() => setShowShare(true)}>
                      Share
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => setShowRename(true)}>Rename</Button>
                </>
              ) : null
            }
          />
        }
        body={
          entries ? (
            <>
              {summary && summary.count > 0 && (
                <p className="text-center text-body-secondary small mb-3" data-testid="trip-totals">
                  {tripSummaryParts(summary).join(' · ')}
                </p>
              )}

              {entries.length > 0 && (
                <LogbookViewControls
                  showViewToggle={false}
                  showNotes={viewPrefs.showNotes}
                  onShowNotesChange={(value) => setViewPref('showNotes', value)}
                  showPhotosToggle
                  showPhotos={viewPrefs.showPhotos}
                  onShowPhotosChange={(value) => setViewPref('showPhotos', value)}
                />
              )}

              {/* Inside the card, never the page's own StatusMessage -- see
                  PhotoErrorAlert, which owns the wording and the dismissal. */}
              <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} />

              {/* TripGroup's Combine row, on the same terms and in the same
                  place: under the dates, because what "before" and "after" mean
                  is decided by them, and carrying the other trip's name because
                  that is what makes the button worth clicking. Nothing renders
                  when this trip has no neighbour. */}
              {entries.length > 0 && (neighbours.previous || neighbours.next) && (
                <div className="d-flex justify-content-center flex-wrap gap-2 mb-3 trip-combine">
                  {neighbours.previous && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => openCombine('previous', neighbours.previous)}
                    >
                      <i className="fa-solid fa-chevron-left me-1" aria-hidden="true"></i>
                      Combine with "{combineLabel(neighbours.previous)}"
                    </Button>
                  )}
                  {neighbours.next && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => openCombine('next', neighbours.next)}
                    >
                      Combine with "{combineLabel(neighbours.next)}"
                      <i className="fa-solid fa-chevron-right ms-1" aria-hidden="true"></i>
                    </Button>
                  )}
                </div>
              )}

              {entries.length === 0 ? (
                <p className="text-center text-body-secondary py-3 mb-0">
                  No entries in this logbook carry that trip name.
                </p>
              ) : (
                <LogbookEntriesTable
                  entries={newestFirst}
                  columns={columns}
                  placeIndex={placeIndex}
                  showNotes={viewPrefs.showNotes}
                  onSelectEntry={onSelectEntry}
                  photosByEntry={viewPrefs.showPhotos ? photosByEntry : null}
                  onOpenPhoto={openPhoto}
                />
              )}
            </>
          ) : null
        }
      />

      <RenameTripModal
        show={showRename}
        currentName={heading}
        // The other trips in THIS logbook, for the collision warning. Scoped to
        // the logbook rather than the item for the reason the buckets are: the
        // per-trip page reads through listCompletedTasksForLogbook, so two
        // Captain's Logs on one boat are separate universes.
        otherTripNames={otherTripNames}
        // A trip with an id doesn't merge into a name it happens to share.
        merges={!isTripGroupIdKey(decodedKey)}
        onHide={() => setShowRename(false)}
        onRename={handleRename}
      />

      <CombineTripsModal
        show={combining !== null}
        direction={combining?.direction}
        thisTrip={{
          key: decodedKey,
          name: heading,
          named: isNamedGroupKey(decodedKey),
          dateRange: summary ? tripGroupDateRange(summary) : '',
          countLabel: entryCountLabel(entries?.length ?? 0),
        }}
        otherTrip={combining && {
          key: combining.row.key,
          name: combineLabel(combining.row),
          named: combining.row.named,
          dateRange: tripGroupDateRange({
            startDate: combining.row.startDate,
            endDate: combining.row.endDate,
          }),
          countLabel: entryCountLabel(combining.row.count),
        }}
        losses={combineLosses}
        onHide={() => setCombining(null)}
        onCombine={(name) => handleCombine(combining.row.key, name)}
      />

      <ShareLogbookTripModal
        show={showShare}
        onHide={() => setShowShare(false)}
        itemId={itemId}
        logbookId={logbookId}
        tripKey={decodedKey}
        tripName={heading}
      />
    </div>
  );
};

export default LogbookTrip;
